import React, { useState, useEffect } from "react";
import axios from "axios";
import styled from "styled-components";
import {
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaKey,
  FaShieldAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { validatePassword, disableSetup2Fa } from "components/API/ApiEnpoints";
import openNotification from "components/utils/CustomNotification";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import ConfirmationDialog from "components/utils/ConfirmationDialog";
import ProfileSessionModal from "../utils/ProfileSessionModal";
import HistoryModal from "../utils/HistoryModal";
import { FiEye, FiEyeOff } from "react-icons/fi";

const Security: React.FC = () => {
  const [twoFaStatus, setTwoFaStatus] = useState<boolean>(false);
  const [expand, setExpand] = useState<boolean>(false);
  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [backupCode, setBackupCode] = useState<boolean>(false);
  const [showDeleteConsentDialog, setShowDeleteConsentDialog] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [userSessions, setUserSessions] = useState<any[]>([]);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [notifyFeedbackUpdates, setNotifyFeedbackUpdates] = useState<boolean>(false);
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const navigate = useNavigate();
  const orgUid = localStorage.getItem("org_uid") || "";
  const historyUid = localStorage.getItem("history_uid");

  // const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);


  const getUserProfile = async () => {
    try {
      const response = await axios.get(`${beAddress}/api/user-profile`, {
        headers: {
          "user-uid": userUid,
        }, withCredentials: true,
      });
      if (response.status === 200) {
        if (response.data.status === "Success") {
          setTwoFaStatus(response.data.data?.two_fa_enabled);
          setBackupCode(response.data.data?.backup_code_enabled);
          setNotifyFeedbackUpdates(response.data.data?.notify_feedback_updates || false);
          //setDisableButton(response.data?.data?.org_two_fa_enabled || false);
        } else {
          openNotification({
            type: "error",
            message: response.data.message || "Something went wrong",
          });

        }
      }
      SessionExtend();
    } catch (error) {
      openNotification({
        type: "error",
        message: "Failed to get user profile from server",
      });
    }
  };

  const handleDisableTwoFA = async () => {
    const apiResponse = await disableSetup2Fa(sessionUid, userUid);
    if (Object.keys(apiResponse).length > 0) {
      if (apiResponse.status === "Success") {
        openNotification({
          type: "success",
          message: apiResponse.data || apiResponse.message,
        });
        localStorage.setItem("2fa_enabled", "false");
        setTwoFaStatus(false);
        setExpand(false);
        setShowPasswordField(false);
      } else {
        openNotification({
          type: "error",
          message: apiResponse.data || apiResponse.message,
        });
      }
      SessionExtend();
    } else {
      openNotification({
        type: "error",
        message: "Failed to disable 2FA",
      });
    }
  };

  const handlePasswordVerify = async () => {
    const apiResponse = await validatePassword(sessionUid, userUid, password);

    if (Object.keys(apiResponse).length > 0) {
      if (apiResponse.status === "Success") {
        setShowPasswordField(false);

        if (!twoFaStatus) {
          navigate("/two-factor-auth");
        } else {
          await handleDisableTwoFA();
        }
      } else {
        console.log("Response:", apiResponse);
        openNotification({
          type: "error",
          message: apiResponse.message || "Something went wrong",
        });
      }
    } else {
      openNotification({
        type: "error",
        message: "Failed to verify password",
      });
    }
  };

  const viewRecoveryCode = () => {
    navigate("/recovery-code-access", { state: { showRecovery: backupCode } });
  };

  useEffect(() => {
    getUserProfile();
  }, []);

  const handleDeleteUser = async () => {
    try {
      const response = await axios.delete(`${beAddress}/api/delete-user-health-data`, {
        headers: {
          "user-uid": userUid,
        }, withCredentials: true,
      });

      if (response.status === 200 && response.data?.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.message || "User health data deleted successfully.",
        });
      } else {
        openNotification({
          type: "error",
          message: response.data.data || "Failed to delete user health data.",
        });
      }
    } catch (error) {
      console.error("Error deleting user data:", error);
      openNotification({
        type: "error",
        message: "An error occurred while deleting user health data.",
      });
    } finally {
      setShowDeleteUserDialog(false);
    }
  };

  const handleDeleteConsent = async () => {
    try {
      const response = await axios.put(
        `${beAddress}/api/withdraw-user-consent`,
        {},
        {
          headers: {
            "user-uid": userUid,
          }, withCredentials: true,
        }
      );

      if (response.status === 200 && response.data?.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.message || "User consent withdrawn successfully.",
        });
      } else {
        openNotification({
          type: "error",
          message: response.data.data || "Failed to withdraw user consent.",
        });
      }
    } catch (error) {
      console.error("Error withdrawing consent:", error);
      openNotification({
        type: "error",
        message: "An error occurred while withdrawing user consent.",
      });
    } finally {
      setShowDeleteConsentDialog(false);
    }
  };

  const handleFeedbackNotificationToggle = async (value: boolean) => {
    try {
      const userType = localStorage.getItem("user_type") || "";

      const response = await axios.put(
        `${beAddress}/api/user-profile`,
        {
          user_uid: userUid,
          user_type: userType,
          org_uid: orgUid,
          notify_feedback_updates: value
        },
        {
          headers: {
            "user-uid": userUid,
            "Content-Type": "application/json",
          }, withCredentials: true,
        }
      );

      if (response.status === 200 && response.data?.status === "Success") {
        setNotifyFeedbackUpdates(value);
        openNotification({
          type: "success",
          message: `Email feedback updates ${value ? "enabled" : "disabled"} successfully.`,
        });
        SessionExtend();
      } else {
        openNotification({
          type: "error",
          message: response.data.data || "Failed to update notification preference.",
        });
      }
    } catch (error) {
      console.error("Error updating feedback notification preference:", error);
      openNotification({
        type: "error",
        message: "An error occurred while updating notification preference.",
      });
    }
  };


  return (
    <SecurityMainContainer>
      <div className="security-block">
        {/* Email Feedback Updates Toggle */}
        <div className="security-option-box">
          <div className="security-option-content">
            <div>
              <div className="security-header">
                <div className="security-heading">
                  <FaShieldAlt className="security-icon" />
                  Email Feedback Updates
                </div>
              </div>
              <div className="secuirty-sub-div">
                <div className="fa-active-msg">
                  {notifyFeedbackUpdates
                    ? "You will receive email notifications when your feedback is updated."
                    : "You will not receive email notifications for feedback updates."}
                </div>
              </div>
            </div>
            <div className="expand-main-class">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={notifyFeedbackUpdates}
                  onChange={(e) => handleFeedbackNotificationToggle(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Additional Security Options */}
        <div className="security-extra-options">
          {/* View Session */}
          <div
            className="security-option-box"
            onClick={() => setShowSessionsModal(true)}
          >
            <div className="security-option-content">
              <div>
                <div className="security-header">
                  <div className="security-heading">
                    <FaShieldAlt className="security-icon" />
                    View Sessions
                  </div>
                </div>
                <div className="secuirty-sub-div">
                  <div className="fa-active-msg">
                    Check all active login sessions for your account.
                  </div>
                </div>
              </div>
              <div className="expand-main-class">
                <div className="expand-button-view">
                  <FaChevronDown />
                </div>
              </div>
            </div>
          </div>

          {/* View History */}
          <div
            className="security-option-box"
            onClick={() => setShowHistoryModal(true)}
          >
            <div className="security-option-content">
              <div>
                <div className="security-header">
                  <div className="security-heading">
                    <FaKey className="security-icon" />
                    View History
                  </div>
                </div>
                <div className="secuirty-sub-div">
                  <div className="fa-active-msg">
                    View your recent account activities and security events.
                  </div>
                </div>
              </div>
              <div className="expand-main-class">
                <div className="expand-button-view">
                  <FaChevronDown />
                </div>
              </div>
            </div>
          </div>

          {/* Withdraw Consent */}
          <div
            className="security-option-box"
            onClick={() => setShowDeleteConsentDialog(true)}
          >
            <div className="security-option-content">
              <div>
                <div className="security-header">
                  <div className="security-heading">
                    <FaExclamationTriangle className="security-icon" />
                    Withdraw Consent
                  </div>
                </div>
                <div className="secuirty-sub-div">
                  <div className="fa-active-msg">
                    Withdraw your consent to share data with the platform.
                  </div>
                </div>
              </div>
              <div className="expand-main-class">
                <div className="expand-button-view">
                  <FaChevronDown />
                </div>
              </div>
            </div>
          </div>

          {/* Delete Health Data */}
          <div
            className="security-option-box"
            onClick={() => setShowDeleteUserDialog(true)}
          >
            <div className="security-option-content">
              <div>
                <div className="security-header">
                  <div className="security-heading">
                    <FaExclamationTriangle className="security-icon" />
                    Delete Health Data
                  </div>
                </div>
                <div className="secuirty-sub-div">
                  <div className="fa-active-msg">
                    Permanently delete your stored health-related data.
                  </div>
                </div>
              </div>
              <div className="expand-main-class">
                <div className="expand-button-view">
                  <FaChevronDown />
                </div>
              </div>
            </div>
          </div>

        </div>


        {/* Change Password Section */}
        <div className="change-password-box" onClick={() => navigate("/change-password")}>
          <div className="change-password-content">
            <div>
              <div className="security-header">
                <div className="security-heading">
                  <FaKey className="security-icon" />
                  Change Password
                </div>
              </div>
              <div className="secuirty-sub-div">
                <div className="fa-active-msg">
                  Update your account password to keep your account secure.
                </div>
              </div>
            </div>

            <div className="expand-main-class">
              <div
                className="expand-button-view"
                onClick={() => navigate("/change-password")}
              >
                <FaChevronDown />
              </div>
            </div>
          </div>
        </div>


        {/* Two-Factor Authentication Section */}
        <div className="security-main-2fa-box">
          <div className="security-2fa-box">
            <div>
              <div className="security-header">
                <div className="security-heading">
                  <FaShieldAlt className="security-icon" />
                  Two-factor Authentication
                </div>
              </div>
              <div className="secuirty-sub-div">
                <div className="fa-active-msg">
                  Two-Factor authentication is{" "}
                  <b>{twoFaStatus ? "active" : "inactive"}</b> for your account.
                </div>
              </div>
            </div>
            <div className="expand-main-class">
              <div className="expand-button">
                <span
                  className={`status ${twoFaStatus ? "active" : "inactive"}`}
                >
                  {twoFaStatus ? "active" : "inactive"}
                </span>
                <div
                  onClick={() => {
                    setExpand((prev) => !prev);
                    if (expand) setShowPasswordField(false);
                  }}
                  className="expand-button-view"
                >
                  {expand ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>
            </div>
          </div>

          {expand && (
            <div className="twofa-details">
              <div className="twofa-info">
                <span>
                  Protect your account with an extra layer of security by
                  requiring access to your phone.
                </span>
                <span>
                  Once configured, you'll be required to enter both your
                  password and an authentication code from your mobile phone in
                  order to sign into your account.
                </span>
              </div>
              <div className="twofa-note-block">
                <FaExclamationTriangle className="note-icon" />
                <span style={{color:"var(--text-color"}}>
                  Note: It is recommended to set up two-factor authentication
                  for extra security.
                </span>
              </div>

              <button
                className="twofa-setup-button"
                disabled={showPasswordField}
                onClick={() => setShowPasswordField(true)}
              >
                {twoFaStatus
                  ? "Disable two-factor authentication"
                  : "Set up two-factor authentication"}
              </button>

              {/* {showPasswordField && (
                <div className="password-field">
                  <span>Enter Password:</span>
                  <input
                    type="password"
                    placeholder="Enter Password"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPassword(e.target.value)
                    }
                  />
                  <button
                    className="verify-button"
                    disabled={!password}
                    onClick={handlePasswordVerify}
                  >
                    Verify
                  </button>
                </div>
              )} */}

              {showPasswordField && (
                <div className="password-field">
                  <span>Enter Password:</span>

                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter Password"
                      value={password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPassword(e.target.value)
                      }
                    />

                    <span
                      className="eye-icon"
                      onClick={() => setShowPassword(!showPassword)}
                      role="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </span>
                  </div>

                  <button
                    className="verify-button"
                    disabled={!password}
                    onClick={handlePasswordVerify}
                  >
                    Verify
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recovery Code Section */}
        {twoFaStatus && (
          <div className="recovery-code-box">
            <div className="recovery-info">
              <div className="recovery-heading">
                Recovery Codes:{" "}
                <span
                  className={`status ${backupCode ? "active" : "inactive"}`}
                  style={{ padding: "2px 4px", fontSize: "14px" }}
                >
                  {backupCode ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
            <div className="recovery-info-1">
              <div className="recovery-info-1-1">
                Recovery codes can be used to access your account if you lose
                access to your device and cannot receive two-factor
                authentication codes.
              </div>
              <button
                className="view-recovery-code-button"
                onClick={viewRecoveryCode}
              >
                {backupCode ? "View Recovery Code" : "Generate recovery code"}
              </button>
            </div>
          </div>
        )}
      </div>
      <ConfirmationDialog
        show={showDeleteUserDialog}
        message="Are you sure you want to delete this user's health data?"
        onConfirm={handleDeleteUser}
        onCancel={() => setShowDeleteUserDialog(false)}
        onClose={() => setShowDeleteUserDialog(false)}
      />

      <ConfirmationDialog
        show={showDeleteConsentDialog}
        message="Are you sure you want to withdraw consent to share your data?"
        onConfirm={handleDeleteConsent}
        onCancel={() => setShowDeleteConsentDialog(false)}
        onClose={() => setShowDeleteConsentDialog(false)}
      />

      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        params={{ history_uid: historyUid! }}
      />

      {showSessionsModal && (
        <ProfileSessionModal
          data={userSessions}
          onClose={() => setShowSessionsModal(false)}
          userUid={userUid}
          sessionUid={sessionUid}
          org_uid={orgUid}
        />
      )}

    </SecurityMainContainer>
  );
};

export default Security;

const SecurityMainContainer = styled.div`
  width: 100%;
  padding: 1% 0;
  display: flex;
  h3 {
    font-size: 1.5rem;
  }

  .security-block {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  
  .security-icon {
  background-color: rgba(93, 236, 231, 1);
    color: #094752;
    padding: 0.5rem;
    border-radius: 50%;
    font-size: 1.2rem;
}
  
  /* Extra Security Option Boxes */
  .security-extra-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .security-option-box {
    padding: 0.75rem 1rem;
    background-color: var(--card-background);
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease-in-out;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .security-option-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
  }

  .security-option-text {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .security-option-title {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 1rem;
    font-weight: 600;
  }

  .security-option-desc {
    font-size: 0.875rem;
    color: #555;
    margin-left: 2.5rem;
  }

  .change-password-box {
    padding: 0.75rem 1rem;
    background-color: var(--card-background);
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease-in-out;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .change-password-content {
    display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  }

  .change-password-icon {
    background-color: rgba(93, 236, 231, 1);
    color: #094752;
    padding: 0.5rem;
    border-radius: 50%;
    font-size: 1.2rem;
  }

  .change-password-title {
    font-size: 1rem;
    font-weight: 600;
  }

  .change-password-desc {
    font-size: 0.875rem;
    color: #555;
  }
.change-password-text {
  display: flex;
  flex-direction: column;
  gap: 1rem; 
}


  /* Box for Two-Factor Authentication */
  .security-2fa-box {
    display: flex;
    justify-content: space-between;
     align-items: flex-start;
     width: 100%;
     &:hover {
      background-color: var(--card-background);
      transform: translateY(-2px);
    }
  }
  .security-main-2fa-box {
    padding: 0.5rem;
   background-color: var(--card-background);
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    /* justify-content: space-between;
    align-items: center; */
  gap: 1rem;
  }

  .security-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
.security-heading {
  display: flex;
  align-items: center;
  gap: 1rem; 
  font-size: 1rem;
  font-weight: 600;
}
  .status {
    padding: 0.5rem;
    border-radius: 0.5rem;
  }

  .status.active {
    background-color: green;
    color: white;
  }

  .status.inactive {
    background-color: red;
    color: white;
  }
  .expand-main-class {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }
  .expand-button {
    display: flex;
    gap: 1rem;
    /* justify-content: flex-end; */

    button {
      /* background-color: var(--background-color);
      color: var(--menu-item-color);
      padding: 0.5em 1em;
      border-radius: 0.5rem;
      border: none;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 600; */

      &:hover {
        background-color: rgba(93, 236, 231, 1);
      }

      &:disabled {
        background-color: #ccc;
      }
    }
  }
  .expand-button-view {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .fa-active-msg {
    /* color: green; */
    margin-left: 2.5rem; 
    font-size: 0.875rem;
    padding: 0.8rem;
  }
  .secuirty-sub-div {
    /* display: flex;
    justify-content: space-between; */
  }

  .twofa-details {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin-top: 1rem;
    padding: 0.5rem;
    .twofa-info {
      margin-bottom: 1rem;
      font-size: 0.875rem;
      .span {
        font-size: 0.875rem;
        padding: 0.5rem;
      }
    }
    .twofa-note-block {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5rem;
      padding: 1rem;
      font-size: 0.9rem;
      color: #555;
      flex-wrap: wrap; // Allow wrapping on smaller screens

      .note-icon {
        color: #e6a000;
        font-size: 1.2rem;
      }

      @media (max-width: 600px) {
        flex-direction: column; // Stack icons and text vertically
        gap: 0.75rem;
        padding: 0.75rem;
      }
    }

    .twofa-setup-button {
      background-color: rgba(93, 236, 231, 1);
      color: #094752;
      padding: 0.5em 1em;
      border-radius: 0.5rem;
      border: none;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 600;
      width: 50%;

      &:hover {
        background-color: rgba(93, 236, 231, 1);
      }

      &:disabled {
        background-color: #ccc;
      }
    }

    .password-field {
      margin-top: 1rem;
      display: flex;
      flex-direction: column;

      gap: 1rem;
      width: 50%;
      input {
        padding: 8px 10px;
        border: 1px solid #e2e8f0;
        border-radius: 4px;
        font-size: 0.75rem;
      }

      .verify-button {
        justify-content: center;
        align-items: center;
        padding: 0.5em 1em;
        background-color: rgba(93, 236, 231, 1);
        color: #094752;
        border-radius: 0.5rem;
        border: none;
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 600;
        width: 50%;

        &:disabled {
          background-color: #ccc;
        }
      }
    }
  }

  /* Box for Recovery Code */
  .recovery-code-box {
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    background-color: #f4f4f4;
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }

  .recovery-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .recovery-heading {
    font-size: 1rem;
    font-weight: 600;
  }
  .recovery-info-1 {
    display: flex;
    gap: 2rem;
  }
  .recovery-info-1-1 {
    font-size: 0.875rem;
    padding: 0.8rem;
  }
  .view-recovery-code-button {
    /* display: flex; */
    justify-content: flex-end;
    background-color: rgba(93, 236, 231, 1);
    color: #094752;
    padding: 0.5em 1em;
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
    /* padding: 0.5em 1em; */
    /* border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600; */

    &:hover {
      background-color: rgba(93, 236, 231, 1);
    }

    &:disabled {
      background-color: #ccc;
    }
  }

  @media (max-width: 768px) {
    .security-block {
      padding: 1rem;
    }

    /* .security-2fa-box,
    .recovery-code-box {
      padding: 1rem;
    } */
  }

  /* Toggle Switch Styles */
  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 24px;
  }

  .toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 24px;
  }

  .toggle-slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }

  .toggle-switch input:checked + .toggle-slider {
    background-color: rgba(93, 236, 231, 1);
  }

  .toggle-switch input:checked + .toggle-slider:before {
    transform: translateX(26px);
  }

  .toggle-slider:hover {
    background-color: #b0b0b0;
  }

  .toggle-switch input:checked + .toggle-slider:hover {
    background-color: rgba(73, 206, 201, 1);
  }
  
  .password-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    }


  .password-input-wrapper input {
    width: 100%;
    padding-right: 40px;
    }


  .eye-icon {
    position: absolute;
    right: 12px;
    font-size: 18px;
    cursor: pointer;
    color: #555;
    }


  .eye-icon:hover {
    color: #000;
    }
`;